import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import Loader from 'components/common/Loader'
import ImageComparisonSpace from 'components/ImageComparison/ImageComparisonSpace'
import ImagePreviewSpace from 'components/ImagePreview/ImagePreviewSpace'

import c from './WebviewPage.module.scss'

type WebviewMode = 'preview' | 'comparison' | undefined

const SOURCE_IMAGE_URL_KEY = 'source_image_url'
const PROCESSED_IMAGE_URL_KEY = 'processed_image_url'
const LANGUAGE_KEY = 'lang'
const MAX_RETRY_ATTEMPTS = 3

function WebviewPage() {
  const [params] = useSearchParams()
  const { i18n, t } = useTranslation()

  const [retryCount, setRetryCount] = useState(0)
  const [loadedImagesCount, setLoadedImagesCount] = useState<number>(0)
  const [isImagesLoadFail, setIsImagesLoadFail] = useState<boolean>(false)
  
  // ✅ Добавлен ref для очистки timeout
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const decodeImageUrl = useCallback((urlParam: string | null): string => {
    if (!urlParam) return ''

    try {
      const parts = urlParam.split('?')
      const endpoint = decodeURIComponent(parts[0] || '')
      const queryString = parts[1] || ''

      const timestamp = Date.now()
      const separator = queryString ? '&' : '?'

      return `${endpoint}?${queryString}${separator}_t=${timestamp}`
    } catch (error) {
      console.error('URL decode error:', error)
      return urlParam
    }
  }, [])

  const sourceImageUrl = useMemo(() => {
    return decodeImageUrl(params.get(SOURCE_IMAGE_URL_KEY))
  }, [params, decodeImageUrl])

  const processedImageUrl = useMemo(() => {
    return decodeImageUrl(params.get(PROCESSED_IMAGE_URL_KEY))
  }, [params, decodeImageUrl])

  const mode = useMemo<WebviewMode>(() => {
    if (sourceImageUrl || processedImageUrl) {
      if (sourceImageUrl && processedImageUrl) return 'comparison'
      return 'preview'
    }
    return undefined
  }, [sourceImageUrl, processedImageUrl])

  const isAllImagesLoaded = useMemo<boolean>(() => {
    if (mode === 'preview') return loadedImagesCount === 1
    if (mode === 'comparison') return loadedImagesCount === 2
    return true
  }, [mode, loadedImagesCount])

  const handleImagesLoad = useCallback(() => {
    setLoadedImagesCount((prev) => prev + 1)
    setRetryCount(0)
  }, [])

  const handleImagesLoadError = useCallback(() => {
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      console.log(`Retry attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS}`)
      
      // ✅ Сохраняем timeout ID для очистки
      timeoutRef.current = setTimeout(
        () => {
          setRetryCount((prev) => prev + 1)
          setLoadedImagesCount(0)
        },
        1000 * (retryCount + 1),
      )
    } else {
      setIsImagesLoadFail(true)
    }
  }, [retryCount])

  // ✅ Cleanup effect для timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const lang = params.get(LANGUAGE_KEY)
    if (lang === 'ru') i18n.changeLanguage('ru')
    if (lang === 'en') i18n.changeLanguage('en')
  }, [params, i18n])

  useEffect(() => {
    if (mode) {
      console.log('Mode:', mode)
      console.log('Source URL:', sourceImageUrl)
      console.log('Processed URL:', processedImageUrl)
    }
  }, [mode, sourceImageUrl, processedImageUrl])

  if (mode) {
    if (isImagesLoadFail) {
      return (
        <main className={c.main}>
          <p className={c.error}>
            {t('error.webviewImageDownload')}
            {retryCount > 0 &&
              ` (${retryCount}/${MAX_RETRY_ATTEMPTS} ${t('error.attempts')})`}
          </p>
          <button
            type="button"
            className={c.retryButton}
            onClick={() => {
              // ✅ Очищаем timeout при retry
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }
              setIsImagesLoadFail(false)
              setRetryCount(0)
              setLoadedImagesCount(0)
            }}
          >
            {t('error.retry')}
          </button>
        </main>
      )
    }

    return (
      <main className={c.main}>
        {!isAllImagesLoaded && (
          <div className={c.loader}>
            <div className={c.loaderWrapper}>
              <Loader />
            </div>
            <p className={c.loaderText}>
              {t('general.imagesLoading')}
              {retryCount > 0 && ` (${retryCount + 1})`}
            </p>
          </div>
        )}

        {mode === 'preview' && (
          <ImagePreviewSpace
            key={`preview-${retryCount}`}
            imageUrl={sourceImageUrl || processedImageUrl!}
            onLoad={handleImagesLoad}
            onLoadError={handleImagesLoadError}
          />
        )}

        {mode === 'comparison' && (
          <ImageComparisonSpace
            key={`comparison-${retryCount}`}
            imageUrl={sourceImageUrl!}
            sourceUrl={processedImageUrl!}
            onLoad={handleImagesLoad}
            onLoadError={handleImagesLoadError}
          />
        )}
      </main>
    )
  }

  return (
    <main className={c.main}>
      <p className={c.error}>{t('error.webview')}</p>
    </main>
  )
}

export default WebviewPage